import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Grid2, CircularProgress } from "@mui/material";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import FormTitle from "../../../../../views/components/common/form/FormTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import {
  Address,
  AddressUserPrefill,
} from "../../../../../models/Address/Address";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AddCityDialog from "./AddCityDialog";

interface EditAddressPopupProps {
  selectedCustomer?: FilterValue[];
  open?: boolean;
  onClose?: () => void;
  reset?: () => void;
  modal?: boolean;
  paddingTopForModal?: number;
  marginBottomForPopup?: number;
  paddingBottomForPopup?: number;
  editaddressId?: number;
  isNavigationNeeded?: boolean;
  getUpdatedBillingAddress?: (address: Address) => void;
  getUpdatedShippingAddress?: (address: Address) => void;
  setLoadingForPopup?: (value: boolean) => void;
  prefillUserDetails?: AddressUserPrefill;
}

interface CouponsSelectedFilters {
  customer_id: FilterValue[];
}

const AddEditAddress: React.FC<EditAddressPopupProps> = ({
  onClose,
  marginBottomForPopup = 4,
  editaddressId,
  isNavigationNeeded = false,
  getUpdatedBillingAddress,
  getUpdatedShippingAddress,
  setLoadingForPopup,
  selectedCustomer,
  prefillUserDetails,
}) => {
  const { showError, showSuccess } = useNotification();
  const location = useLocation();
  const { addressStore, customerStore } = useStores();
  const queryParams = new URLSearchParams(location.search);
  const Id = editaddressId || queryParams.get("address-id") || "";
  const [, setSearchTrigger] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [cityFilter, setCitiesFilter] = useState<FilterValue[]>([]);
  const [stateFilter, setStateFilter] = useState<FilterValue[]>([]);
  const [countryFilter, setcountryFilter] = useState<FilterValue[]>([]);
  const searchParams = new URLSearchParams(location.search);
  const [addCityPopup, setAddCityPopup] = useState(false);

  const initialFilters: CouponsSelectedFilters = {
    customer_id: selectedCustomer?.length
      ? selectedCustomer
      : searchParams.get("customer_id")
      ? searchParams
          .get("customer_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":");
            return { id: list[0], value: list[1] } as FilterValue;
          })
      : [],
  };

  const [filters, setFilters] =
    useState<CouponsSelectedFilters>(initialFilters);
  const inputCityNameTextRef = useRef<string>("");
  const inputCountryNameTextRef = useRef<string>("");
  const inputStateNameTextRef = useRef<string>("");
  const addressId = queryParams.get("address-id") || 0;
  let apiTimer: any;
  let cityTimer: string | number | NodeJS.Timeout | null | undefined = null;
  let stateTimer: string | number | NodeJS.Timeout | null | undefined = null;
  let countryTimer: string | number | NodeJS.Timeout | null | undefined = null;
  const [forceRender, setForceRender] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    try {
      if (Id) {
        addressStore
          .getAddressById(Number(Id))
          .catch((err) => handleError(err?.response?.data?.message))
          .finally(() => setIsLoading(false));
      } else {
        addressStore.clearAddressDetails();
        addressStore.prefillAvailableUserDetails(prefillUserDetails);
      }
    } catch (err: any) {
      handleError(err?.response?.data?.message);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id, addressStore]);

  const handleError = (message?: string) => {
    showError(
      "Error",
      message || "Something went wrong! Please try again later."
    );
  };

  useEffect(() => {
    if (selectedCustomer?.length) {
      addressStore.updateCustomerId(selectedCustomer[0]);
    }
  }, [filters.customer_id]);

  useEffect(() => {
    if (addCityPopup) {
      addressStore.setPopupCountryFilter(countryFilter);
    }
  }, [addCityPopup, countryFilter]);

  const saveDetails = () => {
    if (selectedCustomer) {
      addressStore.updateCustomerId(selectedCustomer[0]);
    }
    if (Id) {
      updateAddress();
    } else {
      addAddress();
    }
  };

  const addAddress = () => {
    setIsLoading(true);
    if (setLoadingForPopup) {
      setLoadingForPopup(true);
    }
    addressStore
      .createAddress()
      .then((res) => {
        if (getUpdatedBillingAddress) {
          getUpdatedBillingAddress(res?.result);
        }
        if (getUpdatedShippingAddress) {
          getUpdatedShippingAddress(res?.result);
        }
        if (setLoadingForPopup) {
          setLoadingForPopup(false);
        }
        setIsLoading(false);
        if (!isNavigationNeeded && onClose) {
          onClose();
        } else {
          showSuccess("Address created successfully");
        }
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        setIsLoading(false);
        if (setLoadingForPopup) {
          setLoadingForPopup(false);
        }
      });
  };

  const updateAddress = () => {
    setIsLoading(true);
    if (setLoadingForPopup) {
      setLoadingForPopup(true);
    }
    addressStore
      .updateAddressDetails()
      .then((res) => {
        if (getUpdatedBillingAddress) {
          getUpdatedBillingAddress(res?.result);
        }
        if (getUpdatedShippingAddress) {
          getUpdatedShippingAddress(res?.result);
        }
        if (setLoadingForPopup) {
          setLoadingForPopup(false);
        }
        setIsLoading(false);
        if (!isNavigationNeeded && onClose) {
          onClose();
        } else {
          showSuccess("Address updated successfully");
        }
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        setIsLoading(false);
        if (setLoadingForPopup) {
          setLoadingForPopup(false);
        }
      });
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchCountry = async () => {
      try {
        await addressStore
          .getCountry({ page_no: 1 })
          .then((countries) => {
            const filterValues: FilterValue[] = countries.map((category) => ({
              id: String(category.id),
              value: String(category.name),
            }));
            setcountryFilter(filterValues);
          })
          .catch(() => {
            setcountryFilter([]);
          });
      } catch (err) {
        setcountryFilter([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError]);

  const searchCity = (
    country_id: number,
    state_id: number,
    search_text?: string
  ) => {
    if (search_text) {
      inputCityNameTextRef.current = search_text;
    } else {
      inputCityNameTextRef.current = "";
      setForceRender((prev) => prev + 1);
      return;
    }
    setSearchTrigger((prev) => prev + 1);

    if (cityTimer) {
      clearTimeout(cityTimer);
    }

    if (search_text && search_text.length > 2) {
      cityTimer = setTimeout(() => {
        if (inputCityNameTextRef.current === search_text) {
          addressStore
            .getCity({
              page_no: 1,
              search_text: search_text,
              state_id: state_id,
              country_id: country_id,
            })
            .then((cities) => {
              const filterValues: FilterValue[] = cities.map((category) => ({
                id: String(category.id),
                value: String(category.name),
              }));
              setCitiesFilter(filterValues);
            })
            .catch((err) => {
              setCitiesFilter([]);
            });
        }
      }, 700);
    } else {
      addressStore
        .getCity({
          page_no: 1,
          state_id: state_id,
          country_id: country_id,
        })
        .then((cities) => {
          const filterValues: FilterValue[] = cities.map((category) => ({
            id: String(category.id),
            value: String(category.name),
          }));
          setCitiesFilter(filterValues);
        })
        .catch((err) => {
          setCitiesFilter([]);
        });
    }
  };

  const searchCountry = (name: string) => {
    inputCountryNameTextRef.current = name;
    setSearchTrigger((prev) => prev + 1);

    if (countryTimer) {
      clearTimeout(countryTimer);
    }

    if (name.length > 2) {
      countryTimer = setTimeout(() => {
        if (inputCountryNameTextRef.current === name) {
          addressStore
            .getCountry({ page_no: 1, search_text: name })
            .then((countries) => {
              const filterValues: FilterValue[] = countries.map((category) => ({
                id: String(category.id),
                value: String(category.name),
              }));
              setcountryFilter(filterValues);
            })
            .catch((err) => {
              setcountryFilter([]);
            });
        }
      }, 500);
    }
  };

  const handleCountrySelect = (country: FilterValue[]) => {
    inputStateNameTextRef.current = "";
    if (country.length === 0) {
      addressStore.updateCountry([]);
      addressStore.updateState([]);
      setStateFilter([]);
    } else {
      addressStore.updateCountry(country);
      searchState(addressStore.addressDetails.country_id!);
    }
  };

  const handleStateSelect = (state: FilterValue[]) => {
    if (state.length === 0) {
      addressStore.updateState([]);
      addressStore.updateCity([]);
      setCitiesFilter([]);
    } else {
      addressStore.updateState(state);
      searchCity(
        addressStore.addressDetails.country_id!,
        addressStore.addressDetails.state_id!
      );
    }
  };

  const handleCitySelect = (cities: FilterValue[]) => {
    if (cities.length === 0) {
      addressStore.updateCity([]);
    } else {
      addressStore.updateCity(cities);
    }
  };

  const searchState = (country_id: number, name?: string) => {
    if (name) {
      inputStateNameTextRef.current = name;
    } else {
      inputStateNameTextRef.current = "";
      setForceRender((prev) => prev + 1);
      return;
    }
    setSearchTrigger((prev) => prev + 1);

    if (stateTimer) {
      clearTimeout(stateTimer);
    }

    if (name && name.length > 2) {
      stateTimer = setTimeout(() => {
        if (inputStateNameTextRef.current === name) {
          addressStore
            .getState({ page_no: 1, search_text: name, country_id: country_id })
            .then((states) => {
              const filterValues: FilterValue[] = states.map((category) => ({
                id: String(category.id),
                value: String(category.name),
              }));
              setStateFilter(filterValues);
            })
            .catch((err) => {
              setStateFilter([]);
            });
        }
      }, 700);
    } else {
      addressStore
        .getState({ page_no: 1, country_id: country_id })
        .then((states) => {
          const filterValues: FilterValue[] = states.map((category) => ({
            id: String(category.id),
            value: String(category.name),
          }));
          setStateFilter(filterValues);
        })
        .catch((err) => {
          setStateFilter([]);
        });
    }
  };

  const resetData = () => {
    addressStore.discardChanges();
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {});
      }, 1500);
  };

  const handleCountrySelectForPopup = (country: FilterValue[]) => {
    addressStore.setSelectedCountry(country);
    addressStore.setSelectedState([]);
    addressStore.setPopupStateFilter([]);

    if (country.length > 0) {
      addressStore
        .getState({
          page_no: 1,
          country_id: Number(country[0].id),
        })
        .then((states) => {
          const filterValues: FilterValue[] = states.map((state) => ({
            id: String(state.id),
            value: String(state.name),
          }));
          addressStore.setPopupStateFilter(filterValues);
        })
        .catch((error) => {
          addressStore.setPopupStateFilter([]);
        });
    }
  };

  const handleStateSelectForPopup = (state: FilterValue[]) => {
    addressStore.setSelectedState(state);
  };

  const handleCountrySearchForPopup = (searchText?: string) => {
    if (countryTimer) {
      clearTimeout(countryTimer);
    }

    if (searchText && searchText.length > 2) {
      countryTimer = setTimeout(() => {
        addressStore
          .getCountry({ page_no: 1, search_text: searchText })
          .then((countries) => {
            const filterValues: FilterValue[] = countries.map((country) => ({
              id: String(country.id),
              value: String(country.name),
            }));
            addressStore.setPopupCountryFilter(filterValues);
          })
          .catch((error) => {
            addressStore.setPopupCountryFilter([]);
          });
      }, 500);
    } else if (!searchText || searchText.length === 0) {
      addressStore
        .getCountry({ page_no: 1 })
        .then((countries) => {
          const filterValues: FilterValue[] = countries.map((country) => ({
            id: String(country.id),
            value: String(country.name),
          }));
          addressStore.setPopupCountryFilter(filterValues);
        })
        .catch((error) => {
          addressStore.setPopupCountryFilter([]);
        });
    }
  };

  const handleStateSearchForPopup = (searchText?: string) => {
    if (addressStore.selectedCountry.length === 0) return;

    if (stateTimer) {
      clearTimeout(stateTimer);
    }

    const countryId = Number(addressStore.selectedCountry[0].id);

    if (searchText && searchText.length > 2) {
      stateTimer = setTimeout(() => {
        addressStore
          .getState({
            page_no: 1,
            search_text: searchText,
            country_id: countryId,
          })
          .then((states) => {
            const filterValues: FilterValue[] = states.map((state) => ({
              id: String(state.id),
              value: String(state.name),
            }));
            addressStore.setPopupStateFilter(filterValues);
          })
          .catch((error) => {
            addressStore.setPopupStateFilter([]);
          });
      }, 700);
    } else if (!searchText || searchText.length === 0) {
      addressStore
        .getState({ page_no: 1, country_id: countryId })
        .then((states) => {
          const filterValues: FilterValue[] = states.map((state) => ({
            id: String(state.id),
            value: String(state.name),
          }));
          addressStore.setPopupStateFilter(filterValues);
        })
        .catch((error) => {
          addressStore.setPopupStateFilter([]);
        });
    }
  };

  const resetPopupState = () => {
    addressStore.setCityName("");
    addressStore.setSelectedCountry([]);
    addressStore.setSelectedState([]);
    addressStore.setManualStateName("");
    addressStore.setManualStateCode(null!);
    addressStore.setPopupStateFilter([]);
  };

  const handleSave = () => {
    if (!addressStore.cityName.trim()) {
      showError("Validation Error", "City name is required");
      return;
    }

    if (addressStore.selectedCountry.length === 0) {
      showError("Validation Error", "Please select a country");
      return;
    }

    if (addressStore.selectedState.length === 0) {
      showError("Validation Error", "Please select a state");
      return;
    }

    if (
      addressStore.selectedState[0].id === "other" &&
      (!addressStore.manualStateName.trim() || !addressStore.manualStateCode)
    ) {
      showError("Validation Error", "Please enter a state name and state code");
      return;
    }

    setIsLoading(true);

    const cityData = {
      cityName: addressStore.cityName.trim(),
      country_id: Number(addressStore.selectedCountry[0].id),
      ...(addressStore.selectedState[0].id === "other"
        ? {
            stateName: addressStore.manualStateName.trim(),
            stateCode: addressStore.manualStateCode || undefined,
          }
        : { stateId: Number(addressStore.selectedState[0].id) }),
    };

    addressStore
      .createCity(cityData)
      .then(() => {
        showSuccess("City added successfully");
        resetPopupState();
        setAddCityPopup(false);

        if (
          addressStore.addressDetails.country_id &&
          addressStore.addressDetails.state_id
        ) {
          searchCity(
            addressStore.addressDetails.country_id,
            addressStore.addressDetails.state_id
          );
        }
      })
      .catch((error: any) => {
        showError(
          "Error occurred",
          error?.response?.data?.message ||
            "Something went wrong! Please try again later."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <>
        <Grid2 container spacing={8} columns={{ lg: 12, xs: 12 }}>
          <Grid2 size={{ lg: 12, md: 12, xs: 12 }}>
            <Paper elevation={1} sx={{ padding: 4, mb: marginBottomForPopup }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <CustomTypography variant="heading4">
                  {Id ? "Edit Address" : "Add Address"}
                </CustomTypography>

                <AppButton
                  variant="grayscale"
                  onClick={() => setAddCityPopup(true)}
                  label="Add City"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <FormTitle title="Customer" fontSize="12px" />
                <AppSearchDropDown
                  isMultiSelect={false}
                  title="Select Customers"
                  value={
                    addressStore.addressDetails.customer_id
                      ? [
                          {
                            id: String(addressStore.addressDetails.customer_id),
                            value:
                              String(
                                addressStore.addressDetails.customer_name
                              ) || "",
                          },
                        ]
                      : filters.customer_id
                  }
                  fontSize="12px"
                  list={customerStore.customers.map((item) => {
                    return { id: String(item.id), value: item.name! };
                  })}
                  local={false}
                  handleSearch={handleCustomerSearch}
                  disabled={Boolean(selectedCustomer?.length)}
                  handleSelect={(items) => {
                    if (items && items.length > 0) {
                      addressStore.updateCustomer(items[0]);
                      setFilters((prev) => ({
                        ...prev,
                        customer_id: items,
                      }));
                    } else {
                      addressStore.updateCustomer();
                      setFilters((prev) => ({
                        ...prev,
                        customer_id: [],
                      }));
                    }
                  }}
                  required
                />
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: -2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="First Name" fontSize="12px" />
                  <FormSmallTextField
                    required
                    placeholder="First name"
                    fontSize="12px"
                    variant="outlined"
                    label="First Name"
                    value={addressStore.addressDetails.first_name || ""}
                    onChange={(id, value) =>
                      addressStore.updateFirstName(value || "")
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Last Name" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Last name"
                    fontSize="12px"
                    variant="outlined"
                    label="Last Name"
                    value={addressStore.addressDetails?.last_name || ""}
                    onChange={(id, value) =>
                      addressStore.updateLastName(String(value))
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: -2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Address 1" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Address 1"
                    label="Address 1"
                    fontSize="12px"
                    variant="outlined"
                    value={addressStore.addressDetails?.address1 || ""}
                    onChange={(id, value) =>
                      addressStore.updateAddressOne(String(value))
                    }
                    required
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Address 2" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Address 2"
                    label="Address 2"
                    fontSize="12px"
                    variant="outlined"
                    value={addressStore.addressDetails?.address2 || ""}
                    onChange={(id, value) =>
                      addressStore.updateAddressTwo(String(value))
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: -2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Address 3" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Address 3"
                    label="Address 3"
                    fontSize="12px"
                    variant="outlined"
                    value={addressStore.addressDetails?.address3 || ""}
                    onChange={(id, value) =>
                      addressStore.updateAddressThree(String(value))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1, mb: 4 }}>
                  <FormTitle title="Country" fontSize="12px" />
                  <AppSearchDropDown
                    isMultiSelect={false}
                    required
                    title="Select Country"
                    value={
                      addressStore.addressDetails.country_id
                        ? [
                            {
                              id: String(
                                addressStore.addressDetails.country_id
                              ),
                              value:
                                addressStore.addressDetails.country_name || "",
                            },
                          ]
                        : []
                    }
                    fontSize="12px"
                    list={countryFilter}
                    local={false}
                    handleSearch={(name) => searchCountry(name!)}
                    textFieldValue={inputCountryNameTextRef.current || ""}
                    handleSelect={(country) => {
                      handleCountrySelect(country);
                      if (inputCountryNameTextRef.current) {
                        inputCountryNameTextRef.current = "";
                      }
                    }}
                  />
                  {/*  */}
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: -2 }}
              >
                <Box sx={{ flex: 1, mb: 4 }}>
                  <FormTitle title="State" fontSize="12px" />
                  <AppSearchDropDown
                    isMultiSelect={false}
                    required
                    title="Select State"
                    value={
                      addressStore.addressDetails.state_id
                        ? [
                            {
                              id: String(addressStore.addressDetails.state_id),
                              value:
                                addressStore.addressDetails.state_name || "",
                            },
                          ]
                        : []
                    }
                    fontSize="12px"
                    list={stateFilter}
                    local={false}
                    handleSearch={(name) =>
                      searchState(
                        addressStore.addressDetails.country_id!,
                        name!
                      )
                    }
                    textFieldValue={inputStateNameTextRef.current}
                    handleSelect={(state) => {
                      //   addressStore.updateState(state);
                      handleStateSelect(state);
                      if (inputStateNameTextRef.current) {
                        inputStateNameTextRef.current = "";
                        setForceRender((prev) => prev + 1);
                      }
                    }}
                    disabled={!addressStore.addressDetails.country_id}
                  />
                  {/*  */}
                </Box>
                <Box sx={{ flex: 1, mb: 4 }}>
                  <FormTitle title="City" fontSize="12px" />
                  <AppSearchDropDown
                    isMultiSelect={false}
                    title="Select City"
                    value={
                      addressStore.addressDetails.city_id
                        ? [
                            {
                              id: String(addressStore.addressDetails.city_id),
                              value:
                                addressStore.addressDetails.city_name || "",
                            },
                          ]
                        : []
                    }
                    fontSize="12px"
                    list={cityFilter}
                    local={false}
                    handleSearch={(name) =>
                      searchCity(
                        addressStore.addressDetails.country_id!,
                        addressStore.addressDetails.state_id!,
                        name!
                      )
                    }
                    textFieldValue={inputCityNameTextRef.current || ""}
                    handleSelect={(city) => {
                      handleCitySelect(city);
                      if (inputCityNameTextRef.current) {
                        inputCityNameTextRef.current = "";
                        setForceRender((prev) => prev + 1);
                      }
                    }}
                    disabled={!addressStore.addressDetails.state_id}
                    required
                  />
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: -2 }}
              >
                <Box sx={{ flex: 1, mb: 0 }}>
                  <FormTitle title="Postalcode" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Postalcode"
                    variant="outlined"
                    fontSize="12px"
                    label="Postalcode"
                    value={addressStore.addressDetails?.postal_code || ""}
                    onChange={(id, value) =>
                      addressStore.updatePostalCode(String(value))
                    }
                    required
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="GSTIN" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Registered GST No."
                    variant="outlined"
                    fontSize="12px"
                    label="GSTIN"
                    value={addressStore.addressDetails?.gstin || ""}
                    onChange={(id, value) =>
                      addressStore.updateGST(String(value))
                    }
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <FormTitle
                    title="Contact No (Include Country Code)"
                    fontSize="12px"
                  />
                  <FormSmallTextField
                    placeholder="Contact No"
                    variant="outlined"
                    fontSize="12px"
                    label="Contact Number"
                    value={addressStore.addressDetails?.contact_no || ""}
                    onChange={(id, value) =>
                      addressStore.updateContact(String(value))
                    }
                    required
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormTitle title="Email" fontSize="12px" />
                  <FormSmallTextField
                    placeholder="Email"
                    variant="outlined"
                    fontSize="12px"
                    label="Email"
                    value={addressStore.addressDetails?.email || ""}
                    onChange={(id, value) =>
                      addressStore.updateEmail(String(value))
                    }
                    required
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 3,
                  justifyContent: "center",
                }}
              >
                {isLoading ? (
                  <>
                    <AppButton
                      label={
                        <>
                          <CircularProgress size={14} color="inherit" />{" "}
                          ...Loading
                        </>
                      }
                      variant="primaryBlock"
                      customStyles={{
                        width: 400,
                      }}
                      onClick={function (): void {}}
                    />
                  </>
                ) : (
                  <AppButton
                    label="Save Details"
                    onClick={() => saveDetails()}
                    customStyles={{
                      width: 400,
                    }}
                  />
                )}

                <AppButton
                  label="Clear All"
                  variant="grayscale"
                  onClick={() => resetData && resetData()}
                  customStyles={{
                    width: 400,
                  }}
                />
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
      </>

      <AddCityDialog
        open={addCityPopup}
        onClose={() => setAddCityPopup(false)}
        isLoading={isLoading}
        cityName={addressStore.cityName}
        selectedCountry={addressStore.selectedCountry}
        selectedState={addressStore.selectedState}
        manualStateName={addressStore.manualStateName}
        manualStateCode={Number(addressStore.manualStateCode)}
        popupCountryFilter={addressStore.popupCountryFilter}
        popupStateFilter={addressStore.popupStateFilter}
        onCityNameChange={(value) => addressStore.setCityName(value)}
        onCountrySelect={handleCountrySelectForPopup}
        onStateSelect={handleStateSelectForPopup}
        onManualStateNameChange={(value) =>
          addressStore.setManualStateName(value)
        }
        onManualStateCodeChange={(value) =>
          addressStore.setManualStateCode(Number(value))
        }
        onCountrySearch={handleCountrySearchForPopup}
        onStateSearch={handleStateSearchForPopup}
        onSave={handleSave}
        onReset={resetPopupState}
      />
    </>
  );
};

export default observer(AddEditAddress);
